import { RouteComponentProps } from "@reach/router";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import styles from "./self-service.module.scss";
import { initialPaginationData } from "../utils/constants";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import Modal from "../components/modal/Modal";
import { resources } from "../utils/resources";
import ContextMenu, {
  ContextOption,
} from "../components/context-menu/ContextMenu";
import style from "./history.module.scss";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import { ClassName, Historys } from "../utils/helpers/types";
import { getAllHistory } from "../utils/helpers/data/history";
import RangePicker from "../components/range-picker/RangePicker";
import Button from "../components/button/Button";
import Select, { Option } from "../components/select/Select";
import { axiosInstanceTasks } from "../utils/helpers/axios";
import { fetchClassNames } from "../utils/helpers/data/file";

const statusMap: Record<string, string> = {
  COMPLETED: "success",
  FAILED: "error",
};
const statusOptions: Option[] = [
  {
    label: "COMPLETED",
    value: "COMPLETED",
  },
  {
    label: "FAILED",
    value: "FAILED",
  },
  {
    label: "PENDING",
    value: "PENDING",
  },
  {
    label: "STARTED",
    value: "STARTED",
  },
  {
    label: "DUPLICATE",
    value: "DUPLICATE",
  },
  {
    label: "INCOMPLETE",
    value: "INCOMPLETE",
  },
  {
    label: "ERROR",
    value: "ERROR",
  },
  {
    label: "INTERRUPTED",
    value: "INTERRUPTED",
  },
];

const dateFilterOptions: Option[] = [
  {
    label: "Processing Time",
    value: "processingTime",
  },
  {
    label: "File Reference Date",
    value: "fileReferenceDate",
  },
  {
    label: "Start Time",
    value: "runningStartTime",
  },
  {
    label: "End Time",
    value: "runningEndTime",
  },
];
const AllHistories: FunctionComponent<RouteComponentProps> = () => {
  let timerRef: NodeJS.Timeout;
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [loading, setLoading] = useState(false);
  const { notify } = useContext(LayoutContext);
  const [history, setHistory] = useState<Historys[]>([]);
  const [historyOnView, setHistoryOnView] = useState<Historys | null>(null);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [moduleName, setModuleName] = useState<string | undefined>(undefined);
  const [dateFilterColumn, setDateFilterColumn] = useState<string | undefined>(
    undefined
  );
  const [processorOptions, setProcessorOptions] = useState<Option[]>([]);

  const [reset, setReset] = useState(false);

  useEffect(() => {
    const fetchProcessorFromBackend = async () => {
      try {
        const response = await fetchClassNames();
        if (!response.error) {
          const classNamesData: ClassName[] = response.data || [];
          const options = classNamesData.map((className) => ({
            label: className.className,
            value: className.fullClass,
          }));
          setProcessorOptions(options);
        }
      } catch (error) {
        console.error("Unable to fetch processor: ", error);
      }
    };
    fetchProcessorFromBackend();
  }, []);

  const resetFilters = () => {
    setReset(true);
    setDateRange([null, null]);
    setDateFilterColumn(undefined);
    setStatus("");
    setModuleName("");
    setPagination({ ...pagination, pageNumber: 1 });
  };
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const showResetFilter = status || dateRange[0] || dateRange[1] || moduleName;

  const pageTitle = "History";

  const formatDateForApi = (date: Dayjs | null) => {
    return date ? date.format("YYYY-MM-DD") : "";
  };

  const fetchHistory = async (
    pageNumber: number = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setReset(false);
    setLoading(true);

    if (dateRange[0] && dateRange[1] && !dateFilterColumn) {
      notify("error", "Please select a filter option for the date range.");
      setLoading(false);
      return;
    }

    const formattedStartTime = formatDateForApi(dateRange[0]);
    const formattedEndTime = formatDateForApi(dateRange[1]);

    const { data, error, message } = await getAllHistory(
      pageNumber,
      pageSize,
      status,
      formattedStartTime,
      formattedEndTime,
      dateFilterColumn,
      moduleName
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch history: ${message}`);
      return;
    }

    setHistory(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total,
    });
    setLoading(false);
  };

  const columns: Column<Historys>[] = [
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
    },
    {
      title: "Start Time",
      dataIndex: "runningStartTime",
      key: "runningStartTime",
      render: (runningStartTime: string | undefined) =>
        runningStartTime
          ? dayjs(runningStartTime).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
    },
    {
      title: "End Time",
      dataIndex: "runningEndTime",
      key: "runningEndTime",
      render: (runningEndTime: string | undefined) =>
        runningEndTime
          ? dayjs(runningEndTime).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
    },
    {
      title: "File Reference Date",
      dataIndex: "fileReferenceDate",
      key: "fileReferenceDate",
      render: (fileReferenceDate: string) =>
        fileReferenceDate
          ? dayjs(fileReferenceDate).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
    },
    {
      title: "Processing Time",
      dataIndex: "processingTime",
      key: "processingTime",
      render: (processingTime: string | undefined) =>
        processingTime
          ? dayjs(processingTime).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => (
        <span className={`tag ${statusMap[value]}`}>{value}</span>
      ),
    },
    {
      title: "",
      render: (_: unknown, history: Historys) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              setHistoryOnView(history);
            },
          },
          (history.status === "FAILED" ||
            history.status === "INCOMPLETE" ||
            history.status === "ERROR" ||
            history.status === "INTERRUPTED") && {
            label: "Retry",
            onClick: () => {
              handleRetry(history.id);
            },
          },
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      },
    },
  ];

  useEffect(() => {
    clearTimeout(timerRef);
    timerRef = setTimeout(() => {
      fetchHistory();
    }, 400);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [pagination.pageSize, reset]);

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });

      if (dateRange[0] && dateRange[1]) {
        await fetchHistory(1);
      } else if (status) {
        await fetchHistory(1);
      } else if (moduleName) {
        await fetchHistory(1);
      }
      if (dateRange[0] && dateRange[1] && !dateFilterColumn) {
        notify("error", "Please select a filter option for the date range.");
        setLoading(false);
        return;
      }

      if (dateRange[0] && !dateRange[1]) {
        notify("error", "Kindly select the two dates.");
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      notify("error", "An error occurred while fetching history.", error);
    }
  };

  const handleRetry = async (historyId: number) => {
    try {
      setLoading(true);
      const response = await axiosInstanceTasks.post(
        `/history/retry/${historyId}`
      );
      if (response.data.message === "Success") {
        notify("success", response.data.data);
      } else {
        notify("error", response.data.data || "Retry failed");
      }
    } catch (error) {
      notify("error", `An error occurred during retry: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">{pageTitle}</h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group">
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="D MMM YYYY"
                  placeholder="Select date range"
                />
              </div>

              <div className="input-group">
                <Select
                  options={dateFilterOptions}
                  value={dateFilterColumn || ""}
                  onSelect={(value) => setDateFilterColumn(value as string)}
                  placeholder="Select Date Filter"
                  responsive
                />
              </div>

              <div className="input-group ">
                <Select
                  options={statusOptions}
                  value={status || ""}
                  onSelect={(value) => setStatus(value as string)}
                  placeholder="Select Status"
                  responsive
                />
              </div>

              <div className="input-group ">
                <Select
                  options={processorOptions}
                  value={moduleName || ""}
                  onSelect={(value) => setModuleName(value as string)}
                  placeholder="Select Processor"
                  responsive
                />
              </div>
            </div>
          </div>

          <div className={style.inputView}>
            <Button
              type="accent"
              size="large"
              className="strong no-pdf-print"
              onClick={handleViewReport}
            >
              Filter record
            </Button>
            <span className={style.button} />
          </div>
        </div>

        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>
      <Table
        dataSource={history || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchHistory}
        onPageSizeChange={(pageSize) =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <TaskViewModal
        history={historyOnView}
        cancel={() => setHistoryOnView(null)}
        refresh={() => fetchHistory()}
        visible={Boolean(historyOnView)}
      />
    </main>
  );
};

interface TaskEditModalProps {
  history: Historys | null;
  cancel: () => void;
  refresh: (taskName: string, pageNumber?: number) => void;
  visible: boolean;
}

const TaskViewModal: FunctionComponent<TaskEditModalProps> = ({
  history: _history,
  cancel,
  visible,
  refresh,
}) => {
  const [loading] = useState(false);
  const [history, setHistory] = useState<Historys | null>(_history);

  useEffect(() => {
    setHistory(_history);
  }, [_history]);

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Task Name</span>
            <span className={styles.value}>{history?.taskName || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Running End Time:</span>
            <span className={styles.value}>
              {history?.runningEndTime || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Running Start Time:</span>
            <span className={styles.value}>
              {history?.runningStartTime || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Status:</span>
            <span className={styles.value}>{history?.status || "N/A"}</span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Summary:</span>
            <span
              className={styles.value}
              dangerouslySetInnerHTML={{
                __html: history?.summary?.replace(/\n/g, "<br>") || "N/A",
              }}
            />
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Processing Time:</span>
            <span className={styles.value}>
              {history?.processingTime || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>File Reference Date:</span>
            <span className={styles.value}>
              {history?.fileReferenceDate || "N/A"}
            </span>
          </div>
          <div className={styles.detail}>
            <span className={styles.key}>Records Processed</span>
            <span className={styles.value}>
              {history?.recordsProcessed || "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default AllHistories;
